import { createContext, ReactNode, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { isPremiumClub } from 'app/clubs/utils'
import { isValidCompanyEmail } from 'utils'
import useClubDetailJoinButtonForInvite from './useClubDetailJoinButtonForInvite'
import useClubDetailJoinButtonPublic from './useClubDetailJoinButtonPublic'
import { useClubMachineState } from '../useClubMachineState'
import { ClubType } from '@fable/types'
import { useErrorModal } from 'hooks'

export const useClubDetailJoinButton = () => {
  const { search } = useLocation()
  const { state, machine } = useClubMachineState()
  const loading = state.matches('joining')
  const ctx = state.context
  const club = ctx.club
  const clubType = ctx.clubType as ClubType
  const isPremium = !!isPremiumClub(club)
  const isInvite = !!new URLSearchParams(search).get('invite') || !!ctx.inviteId
  const joined = state.matches('done.joined')
  const privateClub = club?.access_type === 'private' && !joined

  const allowListPattern = ctx.invite?.company?.email_whitelist_pattern
  const invalidCompanyEmail =
    !!ctx.user &&
    !!allowListPattern &&
    !isValidCompanyEmail({
      email: ctx.user.email,
      pattern: ctx.invite?.company?.email_whitelist_pattern,
    })

  const hookForInvite = useClubDetailJoinButtonForInvite({
    joined,
    state,
    machine,
  })

  const hookForPublic = useClubDetailJoinButtonPublic({
    isPremium,
    state,
    club,
    clubType,
  })

  const handleClick = useMemo(
    () => (isInvite ? hookForInvite.handleClick : hookForPublic.handleClick),
    [hookForInvite.handleClick, hookForPublic.handleClick, isInvite]
  )

  const title = useMemo(() => {
    if (!joined) {
      if (isInvite) return hookForInvite.title
      return hookForPublic.title
    }

    return 'Open club in app'
  }, [isInvite, joined, hookForInvite.title, hookForPublic.title])

  const shouldDisableButton = () => {
    if (!privateClub) return false
    if (ctx.isMember) return false

    const invalidInvite = !ctx.user && !ctx.invite
    const invalidUser = !!ctx.user && (!ctx.invite || invalidCompanyEmail)

    return invalidInvite || invalidUser
  }

  useErrorModal(ctx.error || undefined)

  return {
    title,
    shouldDisableButton: shouldDisableButton(),
    loading,
    isInvite,
    handleClick,
  }
}

/**
 * @note
 * exported for testing
 * use ClubDetailJoinButtonContextProvider for convenience
 */
export const ClubDetailJoinButtonContext = createContext<{
  title: string
  shouldDisableButton: boolean | undefined
  loading: boolean
  isInvite: boolean | undefined
  handleClick: () => void
}>({
  title: '',
  shouldDisableButton: undefined,
  loading: false,
  isInvite: undefined,
  handleClick: () => null,
})

export const ClubDetailJoinButtonContextProvider = ({
  children,
}: {
  children: ReactNode
}) => (
  <ClubDetailJoinButtonContext.Provider value={useClubDetailJoinButton()}>
    {children}
  </ClubDetailJoinButtonContext.Provider>
)
